import { Button } from '@achieve/ascend'
import { AchieveLink } from 'components/AchieveLink/AchieveLink'
import { MediaImageStandard, Typography } from 'components/Contentful'
import { CurrentSvg } from 'components/CurrentSvg'
import Link from 'next/link'
import classNames from 'classnames'
import MenuIcon from 'components/Svgs/Menu.js'
import CloseIcon from 'components/Svgs/Close.js'
import { useEffect, useRef, useState } from 'react'
import { useAppRouter } from 'hooks/useAppRouter/useAppRouter'
import { useTriggerEvents } from 'hooks/useTriggerEvents'
import { getProductAndAddDefaultUtmsToLink } from 'utils/queryManipulation'
import { moveAnchorRight, preserveQueryParams, sanitizeUrlParams } from 'utils/shared'

export const QUALIFY_TRACKING_TIMEOUT_MS = 3000

const PERSISTENT_HEADER_EVENTS = {
  callUs: {
    click_id: 'Call Us',
    click_text: 'FDR-Web | Call Us',
    click_type: 'Phone Click',
    nav_link_section: 'Persistent Header',
    track_event: 'global_header',
    event_action: 'click',
    event_type: 'track',
  },
  qualify: {
    click_id: 'See if you qualify',
    click_text: 'FDR-Web | See if you qualify',
    click_type: 'Button Click',
    nav_link_section: 'Persistent Header',
    track_event: 'button_click',
    event_action: 'button_click',
    event_type: 'track',
  },
}

// Same URL pipeline as AchieveLink, so every way of opening the CTA keeps the page query params
const buildQualifyHref = (linkUrl, queryString) =>
  sanitizeUrlParams(
    moveAnchorRight(getProductAndAddDefaultUtmsToLink(preserveQueryParams(linkUrl, queryString)))
  )

export const FDRHeader = ({
  siteEnv,
  tp_text,
  tp_image,
  linkUrl,
  phoneData,
  logoLink,
  mainProductConfig,
  backLink,
  logoEvent,
  className,
  partnerLogo,
  ariaHidden,
  onMenuClick,
  menuOpen = false,
}) => {
  const phoneNumber = phoneData?.phoneNumber
    ? phoneData.phoneNumber.replace('(', '').replace(')', '').replace(' ', '-')
    : '800-910-0065'
  const { fireAndForget, waitForNavigation } = useTriggerEvents()
  const { searchParams } = useAppRouter()
  const queryString = searchParams?.toString() ?? ''
  const isNavigatingRef = useRef(false)
  const rootRef = useRef(null)
  // Resolved on the client only, so the rendered href still matches the server markup
  const [qualifyHref, setQualifyHref] = useState(linkUrl)

  useEffect(() => {
    setQualifyHref(linkUrl ? buildQualifyHref(linkUrl, queryString) : linkUrl)
  }, [linkUrl, queryString])

  // Keeps the hidden header out of the tab order; aria-hidden alone does not. Set as a DOM
  // property so it applies on the client without changing the server markup
  useEffect(() => {
    if (rootRef.current) rootRef.current.inert = Boolean(ariaHidden)
  }, [ariaHidden])

  useEffect(() => {
    const handlePageShow = (e) => {
      if (e.persisted) isNavigatingRef.current = false
    }
    window.addEventListener('pageshow', handlePageShow)
    return () => window.removeEventListener('pageshow', handlePageShow)
  }, [])

  const handleQualifyClick = async (e) => {
    const event = { ...PERSISTENT_HEADER_EVENTS.qualify, click_url: qualifyHref }

    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button === 1) {
      fireAndForget(event)
      return
    }

    e.preventDefault()
    if (isNavigatingRef.current) return
    isNavigatingRef.current = true

    await waitForNavigation(event, QUALIFY_TRACKING_TIMEOUT_MS).catch(() => {})
    window.location.href = qualifyHref
  }

  return (
    <div
      ref={rootRef}
      aria-hidden={ariaHidden}
      className={classNames(
        'fixed top-0 left-0 right-0 z-100 py-0 shadow-[0_2px_2px_-2px_rgba(0,0,0,0.25)] lg:bg-white lg:py-4',
        className
      )}
    >
      <div className="mx-auto hidden w-full max-w-[1440px] flex-row items-center justify-between gap-x-5 bg-white px-2 lg:flex lg:px-8">
        {logoLink ? (
          <AchieveLink
            track={{
              ...{
                list_name: 'Header Logo',
                click_id: `${mainProductConfig[siteEnv || 'achieve']?.['HeaderLogo']}`,
                click_text: `${
                  mainProductConfig[siteEnv || 'achieve']?.['MainProductHyphen']
                } | Header logo`,
                nav_link_section: 'Header logo',
                track_event: 'global_header',
                event_action: 'link_click',
                event_type: 'link_click',
              },
              ...logoEvent,
            }}
            href={backLink}
            withNextLink
            data-testid={`${String(
              mainProductConfig[siteEnv || 'achieve']?.['MainName']
            )?.toLowerCase()}-header-logo`}
          >
            {partnerLogo ? partnerLogo : <CurrentSvg siteEnv={siteEnv} width={112} height={48} />}
          </AchieveLink>
        ) : partnerLogo ? (
          partnerLogo
        ) : (
          <CurrentSvg siteEnv={siteEnv} width={112} height={48} />
        )}
        <Typography
          content="Get credit card debt relief from America’s top debt relief company."
          className="whitespace-nowrap text-sm"
        />
        <div className="flex flex-col items-center gap-2">
          <div className="flex flex-row gap-4">
            <Button
              component="a"
              href={`tel:${phoneNumber}`}
              className="bg-white border-2 border-[#041B93] border-solid text-[#041B93] px-5 py-2 rounded-md no-underline"
            >
              <Typography
                content={`Call ${phoneNumber}`}
                className="whitespace-nowrap text-[#041B93]"
                variant="bodyXs"
                fontWeight="bold"
              />
            </Button>
            {linkUrl && (
              <Link href={linkUrl} className="no-underline">
                <Button className="group bg-[#CB000E] px-4 py-2 rounded-md h-[30px] rounded-md flex items-center gap-2">
                  <Typography
                    content="See if you qualify"
                    className="text-white whitespace-nowrap"
                    variant="bodyXs"
                    fontWeight="bold"
                  />
                </Button>
              </Link>
            )}
          </div>
          <div className="flex flex-row gap-2 lg:block">
            <div className="flex flex-row gap-2 items-center justify-center">
              <div className="w-[180px] h-[28px]">
                <MediaImageStandard content={tp_image} />
              </div>
              <Typography className="text-black text-base" content={tp_text} />
            </div>
          </div>
        </div>
      </div>
      <div className="mx-auto flex w-full flex-row items-center justify-between gap-x-5 shadow-[0_2px_2px_-2px_rgba(0,0,0,0.25)] lg:hidden lg:px-8">
        <div className="flex w-full flex-col items-center">
          {/* Same order as the legacy FDR mobile persistent header: Call Us left, qualify right */}
          <div
            className={classNames('flex w-full flex-row items-center bg-white py-2', {
              'gap-2 px-3': onMenuClick,
              'px-4': !onMenuClick,
            })}
          >
            {onMenuClick && (
              <button
                type="button"
                onClick={onMenuClick}
                className="flex h-6 w-6 shrink-0 items-center justify-center border-0 bg-transparent p-0"
                aria-label={
                  menuOpen ? 'Main navigation menu close button' : 'Main navigation menu button'
                }
                aria-expanded={menuOpen}
                data-testid="persistent-header-menu-button"
              >
                {menuOpen ? (
                  <CloseIcon title="Close button" width={18} height={18} />
                ) : (
                  <MenuIcon />
                )}
              </button>
            )}
            <div
              className={classNames('flex w-full flex-row justify-between', {
                'gap-3': onMenuClick,
                'gap-4': !onMenuClick,
              })}
            >
              <Button
                component="a"
                href={`tel:${phoneNumber}`}
                className="bg-white border-2 border-[#041B93] border-solid text-[#041B93] px-5 py-2 rounded-md no-underline w-full"
                data-testid="persistent-header-call-us"
                onClick={() =>
                  fireAndForget({
                    ...PERSISTENT_HEADER_EVENTS.callUs,
                    click_url: `tel:${phoneNumber}`,
                  })
                }
              >
                <Typography
                  content={`Call Us`}
                  className="whitespace-nowrap text-[#041B93] text-[16px]! leading-6! tracking-normal!"
                  variant="bodyXs"
                  fontWeight="bold"
                />
              </Button>
              {linkUrl && (
                <Link
                  href={qualifyHref}
                  className="no-underline min-w-[205px]"
                  data-testid="persistent-header-qualify"
                  onClick={handleQualifyClick}
                >
                  <Button className="group bg-[#CB000E] px-4 py-2 rounded-md h-[30px] rounded-md flex items-center gap-2 w-full">
                    <Typography
                      content="See if you qualify"
                      className="text-white whitespace-nowrap text-[16px]! leading-4! tracking-[-0.32px]!"
                      variant="bodyXs"
                      fontWeight="bold"
                    />
                  </Button>
                </Link>
              )}
            </div>
          </div>
          {tp_image && (
            <div className="flex h-8 w-full flex-row items-center justify-center gap-2 overflow-hidden bg-[#ECF9F4]">
              <div className="w-[180px] h-[28px] shrink-0">
                <MediaImageStandard content={tp_image} />
              </div>
              {tp_text && (
                <Typography
                  className="min-w-0 truncate text-black text-base font-medium!"
                  content={tp_text}
                />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
